import React, { useState, useEffect, useRef } from 'react';
import { INDUSTRY_SECTORS } from '../data/simatechData';
import { useCms } from '../context/CmsContext';
import { renderIndustryIconById } from '../data/industryIconsData';
import { IndustryVertical } from '../types';
import {
  Factory,
  Cpu,
  Zap,
  Settings,
  Shield,
  Gauge,
  Activity,
  Radio,
  Bot,
  Wind,
  Sun,
  Layers,
  Wrench,
  CircuitBoard,
  ArrowUpRight,
} from 'lucide-react';

interface IndustryWorksSectionProps {
  onSelectIndustry?: (industry: IndustryVertical) => void;
  onOpenInquiry?: (subject?: string) => void;
  onNavigate?: (page: string, target?: string) => void;
}

// =========================================================================
// ULTRA-SLEEK 1.5PX STROKE CREATIVE ANIMATED INDUSTRIAL LINE ICONS (#06242a)
// Crisp minimalist vector line engineering on pure white background
// =========================================================================

// 1. Cable, Wire & Optical Fiber (Sleek 1.5px Drum Spool with Optical Coils & Trace)
const SleekCableIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Drum Flanges Left & Right */}
    <path
      d="M12 12V60M52 12V60"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    <path
      d="M8 12H56M8 60H56"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Inner Core Spool Hub */}
    <rect
      x="22"
      y="18"
      width="20"
      height="36"
      rx="1"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Concentric Wound Cable Strands */}
    <path
      d="M12 24H52M12 32H52M12 40H52M12 48H52"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Center High-Tech Fiber Optical Core Dot */}
    <circle
      cx="32"
      cy="36"
      r="3"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors origin-center"
    />
    {/* Dynamic Wire Exit Lead */}
    <path
      d="M32 54V66M32 66H46"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <circle cx="46" cy="66" r="1.5" fill="#38c9de" className="opacity-0 group-hover:opacity-100 transition-opacity" />
  </svg>
);

// 2. Rolling Mills & Steel Plants (Sleek 1.5px Mill Stand with Rotating Rolls & Hot Strip)
const SleekSteelIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Heavy Structural Mill Housing Column */}
    <path
      d="M10 64V12H54V64"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    <path
      d="M6 12H58M10 64H54"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Top Work Roll with Crosshair Axis */}
    <circle
      cx="32"
      cy="26"
      r="9"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <path
      d="M32 20V32M26 26H38"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Bottom Work Roll with Crosshair Axis */}
    <circle
      cx="32"
      cy="48"
      r="9"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <path
      d="M32 42V54M26 48H38"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Continuous Rolled Steel Strip */}
    <path
      d="M4 37H60"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Dynamic Kinetic Spark Indicator */}
    <path
      d="M44 33L48 37L44 41"
      stroke="#38c9de"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="opacity-0 group-hover:opacity-100 transition-opacity"
    />
  </svg>
);

// 3. Pharmaceutical & Cleanrooms (Sleek 1.5px Bioreactor Flask & Precision Bubbles)
const SleekPharmaIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Precision Flask Rim */}
    <path
      d="M24 10H40"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Conical Reactor Profile */}
    <path
      d="M27 10V22L12 56C10.8 58.8 12.8 62 16 62H48C51.2 62 53.2 58.8 52 56L37 22V10"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Liquid Fill Level Surface Wave */}
    <path
      d="M17 46C23 43 29 49 35 46C41 43 45 48 47 46"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Measurement Graduation Lines */}
    <path
      d="M33 26H38M31 33H38M28 40H38"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Reactive Bio Compound Bubbles */}
    <circle cx="25" cy="52" r="2" stroke="#38c9de" strokeWidth="1.2" />
    <circle cx="37" cy="54" r="1.5" stroke="#38c9de" strokeWidth="1.2" />
    <circle cx="31" cy="30" r="1" fill="#38c9de" className="opacity-0 group-hover:opacity-100 transition-opacity" />
  </svg>
);

// 4. Chemical & Continuous Process (Sleek 1.5px Distillation Column & Pipeline Array)
const SleekChemicalIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Vertical Distillation Column Tower */}
    <rect
      x="20"
      y="10"
      width="24"
      height="52"
      rx="4"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Internal Fractionation Bubble Trays */}
    <path
      d="M20 22H44M20 34H44M20 46H44M20 54H44"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Overhead Gas Condenser Pipeline */}
    <path
      d="M32 10V4M32 4H52V24H44"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Reboiler Bottom Return Loop */}
    <path
      d="M20 46H10V62H20"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Base Mounting Skid */}
    <path
      d="M14 62H50"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#06242a] transition-colors"
    />
    {/* Flow Arrow Spark */}
    <circle cx="52" cy="14" r="1.5" fill="#38c9de" className="opacity-0 group-hover:opacity-100 transition-opacity" />
  </svg>
);

// 5. Rubber, Extrusion & Tire Lines (Sleek 1.5px Extruder Barrel & Helical Screw Flights)
const SleekRubberIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Raw Polymer Feed Hopper */}
    <path
      d="M20 10H44L38 22H26L20 10Z"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Heated Barrel Cylinder Body */}
    <rect
      x="8"
      y="22"
      width="40"
      height="26"
      rx="1"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Rotating Helical Screw Flights */}
    <path
      d="M14 26L20 44M22 26L28 44M30 26L36 44M38 26L44 44"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Tapered Extrusion Die Head */}
    <path
      d="M48 29L58 32V38L48 41"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Continuous Extruded Profile Strip */}
    <path
      d="M58 35H62"
      stroke="#06242a"
      strokeWidth="1.8"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Bed Legs Base */}
    <path
      d="M16 48V62M40 48V62M10 62H46"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#06242a] transition-colors"
    />
  </svg>
);

// 6. Industrial Furnaces & Metallurgy (Sleek 1.5px Melting Crucible & Thermal Wave)
const SleekFurnaceIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Upper Heating Electrodes */}
    <path
      d="M24 6V20M32 4V20M40 6V20"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Refractory Melting Pot Crucible */}
    <path
      d="M12 20H52L48 58C47.5 62 44 65 40 65H24C20 65 16.5 62 16 58L12 20Z"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Molten Metal Bath Line */}
    <path
      d="M17 36H47"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Dynamic Radiant Thermal Flame Plume */}
    <path
      d="M26 50C28 44 31 50 32 42C33 50 36 44 38 50"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Tapping Spout Lip */}
    <path
      d="M52 20L58 16"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <circle cx="32" cy="12" r="1.5" fill="#38c9de" className="opacity-0 group-hover:opacity-100 transition-opacity" />
  </svg>
);

// 7. Water & Treatment Plants (Sleek 1.5px Clarifier Basin & Fluid Cascade Waves)
const SleekWaterIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Upper Water Droplet Icon */}
    <path
      d="M32 6C32 6 22 18 22 24C22 29.5 26.5 34 32 34C37.5 34 42 29.5 42 24C42 18 32 6 32 6Z"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinejoin="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Inner Droplet Reflection Crescent */}
    <path
      d="M28 26C28 28 29.8 30 32 30"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Clarification Tank Basin */}
    <path
      d="M10 40H54V58C54 61.5 51 64 47.5 64H16.5C13 64 10 61.5 10 58V40Z"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinejoin="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Fluid Cascade Ripples */}
    <path
      d="M16 48C21 45 27 51 32 48C37 45 43 51 48 48"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <path
      d="M16 56C21 53 27 59 32 56C37 53 43 59 48 56"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
  </svg>
);

// 8. Oil & Gas (Sleek 1.5px Offshore Rig / Oil Derrick & Pipeline Manifold)
const SleekOilGasIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Derrick Structural Lattice Legs */}
    <path
      d="M20 64L28 14H36L44 64"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Horizontal Girders */}
    <path
      d="M26 26H38M23 40H41M21 54H43"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Diagonal Cross Struts */}
    <path
      d="M26 26L38 40M38 26L23 40M23 40L43 54M41 40L21 54"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Top Crown Block & Gas Flare */}
    <path
      d="M32 14V6"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    <circle cx="32" cy="5" r="2" stroke="#38c9de" strokeWidth="1.2" fill="#38c9de" fillOpacity="0.2" />
    {/* Base Pipeline Skid Manifold */}
    <path
      d="M10 64H54M14 58H50"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#06242a] transition-colors"
    />
  </svg>
);

// 9. Material Handling & Port Logistics (Sleek 1.5px Overhead Gantry Crane & Cargo Container)
const SleekLogisticsIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-20' }) => (
  <svg
    viewBox="0 0 64 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-colors duration-300`}
  >
    {/* Horizontal Bridge Girder */}
    <path
      d="M8 14H56"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Left and Right Inclined Gantry Support Columns */}
    <path
      d="M14 14L8 64M50 14L56 64"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Ground Rail Tracks */}
    <path
      d="M4 64H16M48 64H60"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#06242a] transition-colors"
    />
    {/* Traveling Hoist Trolley */}
    <rect
      x="26"
      y="10"
      width="12"
      height="8"
      rx="1"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Hoist Steel Cable Line */}
    <path
      d="M32 18V32"
      stroke="#06242a"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
    {/* Standardized Freight Cargo Container */}
    <rect
      x="20"
      y="32"
      width="24"
      height="18"
      rx="1"
      stroke="#06242a"
      strokeWidth="1.5"
      className="group-hover:stroke-[#38c9de] transition-colors"
    />
    {/* Container Corrugation Ridges */}
    <path
      d="M26 32V50M32 32V50M38 32V50"
      stroke="#06242a"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="group-hover:stroke-[#1b7481] transition-colors"
    />
  </svg>
);

export const IndustryWorksSection: React.FC<IndustryWorksSectionProps> = ({
  onSelectIndustry,
  onOpenInquiry,
  onNavigate,
}) => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionBgColor = homeData?.industrySectionBgColor || '#ffffff';
  const sectionBadge = homeData?.industriesBadge || homeData?.industrySectionBadge || 'Industries We Serve';
  const sectionHeading = homeData?.industriesHeading || homeData?.industrySectionHeading || 'Industries We Serve';
  const sectionSubheading = homeData?.industriesSubheading || homeData?.industrySectionSubheading || 'Simatech Automation has successfully implemented automation projects across the cable industry and many others.';

  // Dynamic typography overrides
  const headingFontFamily = homeData?.industryHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.industryHeadingFontSize;
  const headingFontWeight = homeData?.industryHeadingFontWeight || '300';
  const headingColor = homeData?.industryHeadingColor || '#06242a';

  const subheadingFontFamily = homeData?.industrySubheadingFontFamily || "'Google Sans Flex', sans-serif";
  const subheadingFontSize = homeData?.industrySubheadingFontSize;
  const subheadingColor = homeData?.industrySubheadingColor || '#475569';

  const cardTitleFontFamily = homeData?.industryCardTitleFontFamily || "'Google Sans Flex', sans-serif";
  const cardTitleFontSize = homeData?.industryCardTitleFontSize;
  const cardTitleFontWeight = homeData?.industryCardTitleFontWeight || '500';
  const cardTitleColor = homeData?.industryCardTitleColor || '#06242a';
  const cardBgColor = homeData?.industryCardBgColor || '#ebedf4';

  const industryList = (homeData?.industrySectors && homeData.industrySectors.length > 0)
    ? homeData.industrySectors
    : ((siteData?.pages?.industry?.sectors && siteData.pages.industry.sectors.length > 0)
      ? siteData.pages.industry.sectors.map((s: any) => ({
          id: s.id,
          sector: s.title || s.sector || s.name || 'Industry Sector',
          title: s.title || s.sector || s.name || 'Industry Sector',
          iconType: s.iconType || s.id || 'factory',
          icon: s.icon || s.iconType || s.id || 'factory',
        }))
      : INDUSTRY_SECTORS);

  const renderIndustryIcon = (item: any) => {
    const iconValue = item.icon || item.iconType || item.id || '';
    return renderIndustryIconById(iconValue, {
      className: 'w-10 h-10 sm:w-12 sm:h-12 stroke-[1.25] text-[#06242a] group-hover:text-[#1b7481] transition-colors',
    });
  };

  return (
    <section
      id="industry"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 border-t border-slate-100 relative overflow-hidden"
      style={{ backgroundColor: sectionBgColor }}
    >

      
      {/* Background Hyperbolic Curved Wave Pattern (Smaller Size & Light Gradient Fade) */}
      <div className="absolute top-0 right-0 w-[420px] sm:w-[540px] md:w-[620px] h-[300px] sm:h-[380px] pointer-events-none overflow-hidden select-none z-0">
        <svg
          viewBox="0 0 1000 650"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full object-cover object-right-top"
          preserveAspectRatio="none"
        >
          <defs>
            {/* Soft Light Gradient Fade */}
            <linearGradient id="waveSoftGrad" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.40" />
              <stop offset="60%" stopColor="#cbd5e1" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#e2e8f0" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="waveLightGrad" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.30" />
              <stop offset="70%" stopColor="#f1f5f9" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Family 1: Vertical-to-Horizontal Hyperbolic Curved Wave Lines */}
          {[
            "M-50,0 C150,260 480,240 1000,100",
            "M0,0 C180,280 500,250 1000,130",
            "M50,0 C210,300 520,260 1000,160",
            "M100,0 C240,320 540,270 1000,190",
            "M150,0 C270,340 560,280 1000,220",
            "M200,0 C300,360 580,290 1000,250",
            "M250,0 C330,380 600,300 1000,280",
            "M300,0 C360,400 620,310 1000,310",
            "M350,0 C390,420 640,320 1000,340",
            "M400,0 C420,440 660,330 1000,370",
            "M450,0 C450,460 680,340 1000,400",
            "M500,0 C480,480 700,350 1000,430",
            "M550,0 C510,500 720,360 1000,460",
            "M600,0 C540,520 740,370 1000,490",
            "M650,0 C570,540 760,380 1000,520",
            "M700,0 C600,560 780,390 1000,550",
            "M750,0 C630,580 800,400 1000,580",
            "M800,0 C660,600 820,410 1000,610",
            "M850,0 C690,620 840,420 1000,640",
          ].map((d, idx) => (
            <path
              key={`wave-1-${idx}`}
              d={d}
              stroke="url(#waveSoftGrad)"
              strokeWidth="1"
              strokeLinecap="round"
            />
          ))}

          {/* Family 2: Intersecting Diagonal Crest Cascade Lines */}
          {[
            "M200,0 C360,200 620,280 1000,500",
            "M300,0 C440,210 670,300 1000,550",
            "M400,0 C520,220 720,320 1000,600",
            "M500,0 C600,230 770,340 1000,650",
            "M600,0 C680,240 820,360 1000,700",
          ].map((d, idx) => (
            <path
              key={`wave-2-${idx}`}
              d={d}
              stroke="url(#waveLightGrad)"
              strokeWidth="1"
              strokeLinecap="round"
            />
          ))}
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* ========================================================================= */}
        {/* 1. SECTION HEADER                                                         */}
        {/* ========================================================================= */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between mb-10 sm:mb-12 lg:mb-14 gap-6 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col items-start text-left max-w-4xl">
            <h2
              className="text-[22px] sm:text-[27px] md:text-[36px] font-light tracking-tight leading-[1.25] mb-3.5 sm:mb-4"
              style={{
                fontFamily: headingFontFamily,
                fontSize: headingFontSize || undefined,
                fontWeight: headingFontWeight,
                color: headingColor,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
            </h2>
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mb-4" />

            <p
              className="text-[16px] font-light leading-relaxed max-w-4xl"
              style={{
                fontFamily: subheadingFontFamily,
                fontSize: subheadingFontSize || undefined,
                color: subheadingColor,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sectionSubheading }} />
            </p>
          </div>

          <button
            type="button"
            onClick={() => (onNavigate ? onNavigate('industry') : (window.location.href = '/industry'))}
            className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-[#1b7481] hover:text-[#06242a] font-medium transition-all pb-1 border-b border-[#1b7481]/30 hover:border-[#1b7481] self-start md:self-end shrink-0 cursor-pointer group"
          >
            <span>Explore All Verticals</span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        {/* ========================================================================= */}
        {/* 2. CONTIGUOUS SEAMLESS 3x3 GRID (9 INDUSTRY VERTICALS WITH OIL & GAS)     */}
        {/* ========================================================================= */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-slate-300/80 items-stretch transition-all duration-700 delay-200 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ backgroundColor: cardBgColor }}
        >
          {industryList.map((item: any, idx: number) => {
            const targetSectorId = item.id || `sector-${idx}`;
            return (
              <div
                key={item.id || idx}
                id={`industry-item-${item.id || idx}`}
                onClick={() => {
                  if (onNavigate) {
                    onNavigate('industry', targetSectorId);
                  } else if (onSelectIndustry) {
                    onSelectIndustry(item);
                  } else {
                    window.location.href = `/industry#${targetSectorId}`;
                  }
                }}
                style={{
                  transitionDelay: `${idx * 40}ms`,
                  backgroundColor: cardBgColor,
                }}
                className="group relative hover:brightness-95 border-r border-b border-slate-300/80 p-7 sm:p-9 flex flex-col items-center justify-between text-center transition-all duration-300 select-none h-full min-h-[210px] sm:min-h-[230px] hover:shadow-inner cursor-pointer"
                title={`View ${item.sector || item.title || 'Industry'} details on Industry page`}
              >
                {/* Sleek Animated Line Art or Custom Icon Container */}
                <div className="w-18 h-20 sm:w-20 sm:h-22 flex items-center justify-center my-auto transition-transform duration-300 group-hover:scale-110">
                  {renderIndustryIcon(item)}
                </div>

                {/* Industry Sector Name */}
                <h3
                  className="text-[15px] tracking-tight leading-snug transition-colors mt-auto pt-3 border-t border-slate-300/60 w-full group-hover:text-[#1b7481]"
                  style={{
                    fontFamily: cardTitleFontFamily,
                    fontSize: cardTitleFontSize || undefined,
                    fontWeight: cardTitleFontWeight,
                    color: cardTitleColor,
                  }}
                >
                  {item.sector || item.title || item.name || 'Industry Sector'}
                </h3>

                {/* Subtle top-right redirect cue icon */}
                <ArrowUpRight className="w-4 h-4 absolute top-4 right-4 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:text-[#1b7481] transition-all" />
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};
